/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { EntityVersion, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';

import {
  JobBasicInfoDto, JobChangeClientDto,
  JobDppDetailsDto,
  JobListFilterDto,
  JobListItemDto,
  JobQuickCreateRequestDto,
  JobQuickCreateResponseDto,
  JobRedoDto,
  JobUpdateDPPDto,
  JobBillingDto,
} from './job.dto';
import {
  JobBasicInfoMapper,
  JobDppDetailsMapper,
  JobListFilterMapper,
  JobListItemMapper,
  JobQuickDetailsMapper,
  JobRedoDtoMapper,
  JobBillingMapper,
} from './job.mapper';
import { GridColumn } from '../../core-ui-grid';

@Injectable()
export class JobRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  createQuick(details: JobQuickCreateRequestDto): Observable<JobQuickCreateResponseDto> {
    return this.httpPost(`${this.bp}/jobs/quick-job`, details, JobQuickDetailsMapper, identity());
  }

  getFinderInit(): Observable<JobListFilterDto> {
    return this.httpGet(`${this.jad}/jobs-for-finder/init-list`, JobListFilterMapper);
  }

  getListInit(): Observable<JobListFilterDto> {
    return this.httpGet(`${this.jad}/jobs/init-list`, JobListFilterMapper);
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(`${this.jad}/jobs`,
                                          filter, JobListFilterMapper, arrayOf(JobListItemMapper));
  }

  findAllCmAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(`${this.jad}/jobs-contract-manager`,
                                          filter, JobListFilterMapper, arrayOf(JobListItemMapper));
  }

  findAllAsAutocompleteDataSourceForFinder(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.jad}/jobs-for-finder`,
      <JobListFilterDto>{ autocomplete: true, ...filter },
      JobListFilterMapper,
      arrayOf(JobListItemMapper));
  }

  findAllAsAutocompleteDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.jad}/jobs`,
      <JobListFilterDto>{ autocomplete: true, showActive: true, ...filter },
      JobListFilterMapper,
      arrayOf(JobListItemMapper));
  }

  getDpp(jobId: ID): Observable<JobDppDetailsDto> {
    return this.httpGet(
      `${this.bp}/jobs/dpp/${jobId}`,
      optimisticLockingOf(JobDppDetailsMapper),
    );
  }

  updateDpp(job: JobUpdateDPPDto): Observable<JobDppDetailsDto> {
    return this.httpPut(
      `${this.bp}/jobs/dpp/${job.id}`,
      job,
      optimisticLockingOf(identity()),
      optimisticLockingOf(JobDppDetailsMapper),
    );
  }

  redo(job: JobRedoDto): Observable<void> {
    return this.httpPut(`${this.bp}/jobs/redo`, job, JobRedoDtoMapper, none());
  }

  getBasic(jobId: ID): Observable<JobBasicInfoDto> {
    return this.httpGet(
      `${this.bp}/jobs/${jobId}/basic-info`,
      JobBasicInfoMapper,
    );
  }

  unfinalize(jobId: ID, version: EntityVersion): Observable<JobBasicInfoDto> {
    return this.httpPost(
      parametrizeUrl(`${this.bp}/jobs/${jobId}/unfinalize`, { version }),
      void 0,
      identity(),
      JobBasicInfoMapper);
  }

  changeClient(jobId: ID, client: JobChangeClientDto): Observable<JobBasicInfoDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-client`,
      client,
      identity(),
      JobBasicInfoMapper,
    );
  }

  reassign(jobId: ID, version: EntityVersion, salespersonId: ID): Observable<void> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/reassign-salesperson`,
      {
        version,
        salespersonId,
      },
      identity(),
      none());
  }

  exportContactManagerList(filter: JobListFilterDto, columns: GridColumn[]):
    Observable<{url: string; fileName: string}> {
    return this.httpPost(
      parametrizeUrl(`${this.jad}/jobs-contract-manager/export?`, filter),
      { columns },
      identity(),
      identity(),
    );
  }

  recalculateJobList(): Observable<any> {
    return this.httpPut(
      `${this.bp}/recalculate-jobs`,
      {},
      identity(),
      identity(),
    );
  }

  validateCM(jobId: ID): Observable<any> {
    return this.httpPost(
      `${this.bp}/jobs-contract-manager/${jobId}/validate`,
      {},
      identity(),
      identity(),
    );
  }

  validateJob(jobId: ID): Observable<any> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/validate`,
      {},
      identity(),
      identity(),
    );
  }

  exportJobsList(filter: JobListFilterDto, columns: GridColumn[]):
    Observable<{url: string; fileName: string}> {
    return this.httpPost(
      parametrizeUrl(`${this.jad}/jobs/export?`, filter),
      { columns },
      identity(),
      identity(),
    );
  }

  getBilling(officeId: ID, jobId: ID): Observable<JobBillingDto> {
    return this.httpGet(
      `${this.jad}/jobs-contract-manager/${jobId}/billing-info?officeId=${officeId}`,
      JobBillingMapper,
    );
  }

  updateBilling(jobId: ID, jobBilling: JobBillingDto): Observable<JobBillingDto> {
    return this.httpPut(
      `${this.jad}/jobs-contract-manager/${jobId}/billing-info`,
      jobBilling,
      JobBillingMapper,
    );
  }
}
